import React, { useEffect, useState } from "react";
import { FaChartLine, FaChartPie, FaProjectDiagram } from "react-icons/fa";
import { HiOutlineChevronDown } from "react-icons/hi2";
import DashboardCard from "./DashboardCard";
import api from "../../../services/api";
import toast from "react-hot-toast";
import { classNames } from "@/utils";
import SelectMonth from "../../../components/selectMonth";

const getBudgetTypeColor = (budgetType) => {
  switch (budgetType) {
    case "project":
      return "bg-green-500";
    case "prediction":
      return "bg-purple-500";
    case "admin":
      return "bg-yellow-500";
    case "invest":
      return "bg-orange-500";
    case "overbudget":
      return "bg-red-500";
    default:
      return "bg-gray-500";
  }
};

const getProjectTypeColor = (projectType) => {
  switch (projectType) {
    case "project":
      return "bg-blue-500";
    case "admin":
      return "bg-gray-500";
    case "timeoff":
      return "bg-pink-500";
    default:
      return "bg-gray-400";
  }
};

const ActivityCard = ({ user, filters, organisation, businessUnit }) => {
  const [date, setDate] = useState(new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)));
  const [teamStats, setTeamStats] = useState({
    totalHours: 0,
    activeProjects: 0,
    teamMembers: 0,
    utilizationRate: 0,
  });
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedView, setSelectedView] = useState("budget"); // "budget" or "project"
  const [showRepartition, setShowRepartition] = useState(false);

  const fetchTeamData = async () => {
    try {
      // Build user search query
      const userQuery = {
        role: ["admin", "user", "super_admin"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };

      // Add manager filter only if a user is selected
      if (user) {
        userQuery.manager_id = user._id;
        userQuery.managees_of_managees = filters.manageesOfManagees;
      }

      // Filter by business unit
      if (businessUnit._id !== "ALL") userQuery.business_unit_id = businessUnit._id;
      else if (organisation?._id && organisation._id !== "ALL") userQuery.organisation_id = organisation._id;

      // Fetch team members
      const { data: usersData, ok } = await api.post(`/user/search`, userQuery);
      
      if (!ok || !usersData?.users) return toast.error("Error fetching users for activity");

      // Fetch activities for stats
      const { data: activitiesData } = await api.post(`/activity/search`, {
        date: date.getTime(),
        userIds: usersData.users.map((u) => u._id),
      });
      setActivities(activitiesData || []);

      // Calculate working days for the selected month (full month or up to today if current month)
      const today = new Date();
      const isCurrentMonth = date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      const lastDayToCount = isCurrentMonth ? Math.min(today.getDate(), daysInMonth) : daysInMonth;
      
      let workingDays = 0;
      for (let day = 1; day <= lastDayToCount; day++) {
        const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) workingDays++;
      }
      const expectedHours = workingDays * 8;

      // Filter out support functions and incoming users, keep daily freelancers
      const allActiveUsers = usersData.users.filter((u) => {
        if (u.non_billable) return false;
        // Exclude users who haven't started yet
        if (u.starting_date && new Date(u.starting_date) > today) return false;
        return true;
      });

      // Calculate hours filled per user and sort ascending (lowest first = needs attention)
      const usersWithHours = allActiveUsers.map((u) => {
        const userActivities = (activitiesData || []).filter((a) => a.userId === u._id);
        const totalHours = userActivities.reduce((acc, a) => acc + (a.total || 0), 0);
        const percentage = expectedHours > 0 ? (totalHours / expectedHours) * 100 : 0;
        const needsAttention = percentage < 50;
        const isWarning = percentage >= 50 && percentage < 80;
        return { ...u, hoursFilled: totalHours, fillPercentage: percentage, needsAttention, isWarning };
      });
      usersWithHours.sort((a, b) => a.fillPercentage - b.fillPercentage);
      setUsers(usersWithHours);

      // Calculate stats
      const totalHours = activitiesData.reduce((acc, activity) => acc + activity.total, 0);
      const uniqueProjects = new Set(activitiesData.map((activity) => activity.projectId)).size;
      const uniqueMembers = new Set(activitiesData.map((activity) => activity.userId)).size;
      const utilizationRate = (totalHours / (uniqueMembers * 160)).toFixed(2); // Assuming 160 working hours per month

      setTeamStats({
        totalHours: (totalHours / 8).toFixed(1), // Convert to days
        activeProjects: uniqueProjects,
        teamMembers: uniqueMembers,
        utilizationRate: (utilizationRate * 100).toFixed(1),
      });
    } catch (error) {
      console.error(error);
      toast.error("Error fetching team data");
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [user?._id, filters, date, businessUnit]);

  // Calculate repartition statistics
  const calculateRepartitionStats = () => {
    const budgetTypeStats = {
      project: { hours: 0, members: new Set(), projects: new Set() },
      prediction: { hours: 0, members: new Set(), projects: new Set() },
      admin: { hours: 0, members: new Set(), projects: new Set() },
      invest: { hours: 0, members: new Set(), projects: new Set() },
      overbudget: { hours: 0, members: new Set(), projects: new Set() },
    };

    const projectTypeStats = {
      project: { hours: 0, members: new Set(), projects: new Set() },
      admin: { hours: 0, members: new Set(), projects: new Set() },
      timeoff: { hours: 0, members: new Set(), projects: new Set() },
    };

    activities.forEach((activity) => {
      const budgetType = activity.budgetType || "other";
      const projectType = activity.projectType || "project";

      // Budget type stats
      if (budgetTypeStats[budgetType]) {
        budgetTypeStats[budgetType].hours += activity.total || 0;
        budgetTypeStats[budgetType].members.add(activity.userId);
        budgetTypeStats[budgetType].projects.add(activity.projectId);
      }

      // Project type stats
      if (projectTypeStats[projectType]) {
        projectTypeStats[projectType].hours += activity.total || 0;
        projectTypeStats[projectType].members.add(activity.userId);
        projectTypeStats[projectType].projects.add(activity.projectId);
      }
    });

    return { budgetTypeStats, projectTypeStats };
  };

  const getStatsForView = () => {
    const { budgetTypeStats, projectTypeStats } = calculateRepartitionStats();
    return selectedView === "budget" ? budgetTypeStats : projectTypeStats;
  };

  const getColorForView = (type) => {
    return selectedView === "budget" ? getBudgetTypeColor(type) : getProjectTypeColor(type);
  };

  const getTypeLabel = (type) => {
    if (selectedView === "budget") {
      switch (type) {
        case "project":
          return "Client Projects";
        case "prediction":
          return "Predictions";
        case "admin":
          return "Administration";
        case "invest":
          return "Investments";
        case "overbudget":
          return "Over Budget";
        default:
          return type;
      }
    } else {
      switch (type) {
        case "project":
          return "Client Projects";
        case "admin":
          return "Admin Tasks";
        case "timeoff":
          return "Time Off";
        default:
          return type;
      }
    }
  };

  const totalHours = activities.reduce((acc, activity) => acc + (activity.total || 0), 0);
  const stats = getStatsForView();

  return (
    <DashboardCard
      title="Hours Filled"
      icon={<FaChartLine className="text-blue-500" />}
      action={
        <div className="flex items-center gap-2">
          <SelectMonth start={0} indexDefaultValue={0} value={date} onChange={(e) => setDate(e.target.value)} showArrows />
        </div>
      }>
      <div className="">
        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-1">
          <button
            onClick={() => setShowRepartition(false)}
            className={`flex-1 py-1 px-3 rounded-md text-sm font-medium transition-colors ${
              !showRepartition ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}>
            <FaChartLine className="inline w-3 h-3 mr-2" />
            Team Overview
          </button>
          <button
            onClick={() => setShowRepartition(true)}
            className={`flex-1 px-3 rounded-md text-sm font-medium transition-colors ${
              showRepartition ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}>
            <FaChartPie className="inline w-3 h-3 mr-2" />
            Repartition
          </button>
        </div>

        {!showRepartition ? (
          // Original Team Overview View
          <div className="space-y-2">
            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-2 px-2">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <h3 className="text-xs font-medium text-gray-500">Total</h3>
                <p className="text-base font-semibold text-gray-800">{teamStats.totalHours}</p>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <h3 className="text-xs font-medium text-gray-500">Projects</h3>
                <p className="text-base font-semibold text-gray-800">{teamStats.activeProjects}</p>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <h3 className="text-xs font-medium text-gray-500">Team</h3>
                <p className="text-base font-semibold text-gray-800">{users.length}</p>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <h3 className="text-xs font-medium text-gray-500">Rate</h3>
                <p className="text-base font-semibold text-gray-800">{teamStats.utilizationRate}%</p>
              </div>
            </div>
            {(() => {
              const { budgetTypeStats } = calculateRepartitionStats();
              const budgetTypesWithHours = ["project", "prediction", "admin", "invest", "overbudget"].filter(
                (budgetType) => budgetTypeStats[budgetType]?.hours > 0
              );
              
              if (budgetTypesWithHours.length === 0) return null;
              
              return (
                <div className="flex flex-wrap gap-2 mt-2">
                  {budgetTypesWithHours.map((budgetType) => (
                    <div key={budgetType} className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded-full ${getBudgetTypeColor(budgetType)}`} />
                      <span className="text-[10px] text-gray-600">{budgetType}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Team Members Details */}
            <div className="space-y-1">
              {users.filter((m) => !(m.contract === "FREELANCE" && m.payment_type === "DAILY")).map((member) => (
                <MemberCard key={member._id} member={member} date={date} />
              ))}
            </div>
            {/* Daily Freelancers Section */}
            {users.filter((m) => m.contract === "FREELANCE" && m.payment_type === "DAILY").length > 0 && (
              <div className="mt-4 pt-3 border-t border-dashed border-gray-300">
                <div className="text-xs font-medium text-orange-600 mb-2 flex items-center gap-1">
                  <span>📅</span> Daily Freelancers
                </div>
                <div className="space-y-1">
                  {users.filter((m) => m.contract === "FREELANCE" && m.payment_type === "DAILY").map((member) => (
                    <MemberCard key={member._id} member={member} date={date} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Repartition View
          <div className="space-y-3">
            {/* Repartition View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSelectedView("budget")}
                className={`flex-1 py-1 px-3 rounded-md text-sm font-medium transition-colors ${
                  selectedView === "budget" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}>
                <FaChartPie className="inline w-3 h-3 mr-2" />
                By Budget Type
              </button>
              <button
                onClick={() => setSelectedView("project")}
                className={`flex-1 py-1 px-3 rounded-md text-sm font-medium transition-colors ${
                  selectedView === "project" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}>
                <FaProjectDiagram className="inline w-3 h-3 mr-2" />
                By Project Type
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                <div className="text-2xl font-bold text-gray-800">{(totalHours / 8).toFixed(1)}</div>
                <div className="text-xs text-gray-500">Total Days</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                <div className="text-2xl font-bold text-gray-800">{users.length}</div>
                <div className="text-xs text-gray-500">Team Members</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                <div className="text-2xl font-bold text-gray-800">{Object.keys(stats).filter((key) => stats[key].hours > 0).length}</div>
                <div className="text-xs text-gray-500">Active Types</div>
              </div>
            </div>

            {/* Type Breakdown */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">{selectedView === "budget" ? "Budget Type Distribution" : "Project Type Distribution"}</h4>

              {Object.entries(stats)
                .filter(([type, data]) => data.hours > 0)
                .sort((a, b) => b[1].hours - a[1].hours)
                .map(([type, data]) => {
                  const percentage = totalHours > 0 ? (data.hours / totalHours) * 100 : 0;
                  const days = (data.hours / 8).toFixed(1);

                  return (
                    <div key={type} className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${getColorForView(type)}`} />
                          <span className="text-sm font-medium text-gray-800">{getTypeLabel(type)}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-800">{days}d</div>
                          <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full ${getColorForView(type)} transition-all duration-300`} style={{ width: `${percentage}%` }} />
                      </div>

                      {/* Additional Stats */}
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>{data.members.size} members</span>
                        <span>{data.projects.size} projects</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

const MemberCard = ({ member, date }) => {
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchMemberActivities = async () => {
    try {
      const { data } = await api.post(`/activity/search`, {
        date: date.getTime(),
        userId: member._id,
      });
      setActivities(data);
    } catch (error) {
      console.error(error);
      toast.error(`Error fetching activities for ${member.name}`);
    }
  };

  useEffect(() => {
    fetchMemberActivities();
  }, [member._id, date]);

  const getTotal = () => (activities.reduce((acc, a) => acc + a.total, 0) / 8).toFixed(2);

  const calculateWorkingDays = () => {
    const today = new Date(new Date().setMonth(new Date().getMonth() - 1));
    const currentDay = today.getDate();
    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

    // Calculate working days up to today
    let workingDays = 0;
    for (let day = 1; day <= Math.min(currentDay, daysInMonth); day++) {
      const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
      const dayOfWeek = currentDate.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  // Calculate percentage of hours filled
  const getPercentage = () => {
    const totalHours = activities.reduce((acc, a) => acc + a.total, 0);
    const workingDays = calculateWorkingDays();
    const expectedHours = workingDays * 8;
    const percentage = (totalHours / expectedHours) * 100;
    return Math.min(percentage, 100).toFixed(1);
  };

  const percentage = getPercentage();

  const calculateBudgetTypePercentages = () => {
    const workingDays = calculateWorkingDays();
    const expectedHours = workingDays * 8;

    const x = activities.reduce((acc, activity) => {
      const percentage = (activity.total / expectedHours) * 100;
      if (!acc[activity.budgetType]) {
        acc[activity.budgetType] = 0;
      }
      acc[activity.budgetType] += percentage;
      return acc;
    }, {});

    const order = ["project", "prediction", "admin", "invest", "overbudget"];
    const sortedBudgetTypePercentages = Object.fromEntries(Object.entries(x).sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0])));

    return sortedBudgetTypePercentages;
  };

  const budgetTypePercentages = calculateBudgetTypePercentages();

  const needsAttention = percentage < 50;
  const isWarning = percentage >= 50 && percentage < 80;

  return (
    <div className={`rounded-lg shadow-sm ${needsAttention ? "bg-red-50 border border-red-200" : isWarning ? "bg-amber-50 border border-amber-200" : "bg-white"}`}>
      <div className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50/50" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <div className="relative">
            <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full object-cover border border-gray-100" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-800">{member.name}</h3>
            <p className="text-[10px] text-gray-400">{member.position} • {member.contract}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="text-xs text-gray-600">{getTotal()}d</div>
            <div className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${needsAttention ? "bg-red-200 text-red-700" : isWarning ? "bg-amber-200 text-amber-700" : "bg-green-100 text-green-700"}`}>{percentage}%</div>
          </div>
          <HiOutlineChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-2 pb-2">
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">
          {Object.entries(budgetTypePercentages).map(([budgetType, percentage]) => (
            <div
              key={budgetType}
              className={`h-full ${getBudgetTypeColor(budgetType)} transition-all duration-300 ease-in-out`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          ))}
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 p-2">
          <div className="space-y-1">
            {activities.map((activity) => (
              <div key={activity._id} className="flex justify-between items-center p-1.5 bg-gray-50 rounded text-xs">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getBudgetTypeColor(activity.budgetType)}`} />
                  <div>
                    <p className="font-medium text-gray-800">{activity.projectName}</p>
                    <p className="text-gray-600">{activity.budgetName}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-800">{(activity.total / 8).toFixed(1)}d</p>
                  <p className="text-gray-500">{activity.budgetType}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
